import { useTranslation } from 'react-i18next'

import CustomButton from '@/components/CustomButton'
import { customerServiceCaseService } from '@/services'
import { useAppSelector } from '@/store/hook'
import { selectUserCountryId } from '@/store/selectors/userSelector'
import { useAsync } from '@/hooks'
import usePermission from '@/hooks/usePermission'
import { ASYNC_STATUS, BUTTON_TYPE } from '@/libs/constants'
import { MODULE_SERVICE_RIGHTS } from '@/libs/constants/modulerights'

const useValidateExcludedMaterialNumber = (
  materialNumber,
  countryId,
  onSuccessHandler,
  onErrorHandler,
) => {
  const { execute: executeValidateMaterialNumber, status } = useAsync(
    async () =>
      customerServiceCaseService.validateMaterialNumber({
        materialNumber,
        countryId,
      }),
    false,
    onSuccessHandler,
    onErrorHandler,
  )

  return {
    executeValidateMaterialNumber,
    isVerifying: status === ASYNC_STATUS.PENDING,
  }
}

interface B2becAvailableServiceButtonProps {
  materialNumber: string
  onSuccess: () => void
  onError: (error: Error) => void
}

const B2becAvailableServiceButton = ({
  materialNumber,
  onSuccess,
  onError,
}: B2becAvailableServiceButtonProps) => {
  const { t } = useTranslation()
  const {
    verifiedPermissions: [canCreateRepairService, canCreateMaintenanceService],
  } = usePermission([
    MODULE_SERVICE_RIGHTS.CREATE_REPAIR_SERVICE_CASE,
    MODULE_SERVICE_RIGHTS.CREATE_MAINTENANCE_SERVICE_CASE,
  ])

  const userCountryId = useAppSelector(selectUserCountryId)
  const { executeValidateMaterialNumber, isVerifying } = useValidateExcludedMaterialNumber(
    materialNumber,
    userCountryId,
    onSuccess,
    onError,
  )

  const isDisabled = isVerifying || (!canCreateRepairService && !canCreateMaintenanceService)

  return (
    <CustomButton
      disabled={isDisabled}
      type={BUTTON_TYPE.ACTION}
      onClick={executeValidateMaterialNumber}
    >
      {isVerifying
        ? t('service.selectMachine.verifyingMachine')
        : t('service.selectMachine.availableService')}
    </CustomButton>
  )
}

export default B2becAvailableServiceButton
